import { ref } from 'vue';
import * as THREE from 'three';
import {
  createFeatureCommandSeq,
  executeFeatureCommitWithHistory,
  type EditorFeatureEditBaselineSnapshot,
  type EditorFeatureHistoryRecord,
} from '@puppycad/cad-core';
import type {
  BossExtrudeSelectionResolution,
  BossRevolveEditSessionPayload,
  CadCommand,
  ChamferEditSessionPayload,
  ChamferFeatureDefinition,
  ExtrudeFeatureDefinitionModel,
  FeatureDefinition,
  FeatureEditSession,
  FilletEditSessionPayload,
  FilletFeatureDefinition,
  FeaturePrototype,
  SerializedFeatureReferenceBinding,
  Viewer,
} from '@puppycad/cad-core';
import type { ExtrudeViewportAdapter } from '@/composables/extrude-types';

/**
 * 视口适配器组合函数参数。
 */
export interface UseEditorViewportAdapterOptions {
  /** 读取当前 Viewer 实例。 */
  getViewer: () => Viewer | null;
  /** 写入模型历史层的 feature 提交记录。 */
  commitFeatureHistoryRecord?: (record: EditorFeatureHistoryRecord) => Promise<void> | void;
}

/**
 * 创建编辑器特征宿主所需的视口适配器。
 *
 * 设计说明：
 * 1. `useExtrudeFeature` 与 `useRevolveFeature` 只依赖统一适配器，不直接耦合 `Viewer` 具体实现；
 * 2. 这里负责把 `Viewer` 的公开能力投影为宿主可消费的最小接口；
 * 3. 该模块不保存业务状态，只负责桥接与错误边界。
 */
export function useEditorViewportAdapter(options: UseEditorViewportAdapterOptions) {
  const { getViewer, commitFeatureHistoryRecord } = options;
  const featureEditBaselines = new Map<string, EditorFeatureEditBaselineSnapshot>();

  /**
   * 获取当前 Viewer，若不存在则抛出明确错误。
   *
   * @returns 已初始化的 Viewer。
   */
  const requireViewer = (): Viewer => {
    const currentViewer = getViewer();
    if (!currentViewer) {
      throw new Error('Viewer 未初始化，无法执行特征宿主操作。');
    }
    return currentViewer;
  };

  /**
   * 从 cad-core feature 编辑会话中提取进入编辑前的命令基线。
   *
   * 设计说明：
   * Viewer 进入编辑会话时会把 Worker 回放到“移除目标及真实下游”的依赖隔离沙箱。
   * 因此属性提交时不能再把当前 Worker 状态当作 before，否则 update 会被误判为 create。
   *
   * @param featureId 正在编辑的 Feature ID。
   * @param session cad-core 创建的 Feature 编辑会话。
   * @returns 可写入 feature history service 的 before 基线。
   */
  const createFeatureEditBaselineSnapshot = (
    featureId: string,
    session: FeatureEditSession | null,
  ): EditorFeatureEditBaselineSnapshot | null => {
    if (!session) {
      return null;
    }

    const targetCommand = session.getTargetCommand() as CadCommand;
    if (targetCommand.id !== featureId) {
      throw new Error(`Feature 编辑会话目标不一致：feature=${featureId}, command=${targetCommand.id}`);
    }

    const originalCommands = session.getOriginalCommands() as readonly CadCommand[];
    const commandIndex = originalCommands.findIndex((command) => command.id === featureId);
    if (commandIndex < 0) {
      throw new Error(`Feature 编辑会话原始历史缺少目标命令：${featureId}`);
    }

    return {
      featureId,
      command: targetCommand,
      commandIndex,
      commandSeq: createFeatureCommandSeq(originalCommands),
    };
  };

  /**
   * 记录 feature 编辑前的主状态基线。
   *
   * @param featureId 正在编辑的 Feature ID。
   * @param session cad-core 创建的 Feature 编辑会话。
   */
  const rememberFeatureEditBaseline = (
    featureId: string,
    session: FeatureEditSession | null,
  ): void => {
    const baseline = createFeatureEditBaselineSnapshot(featureId, session);
    if (!baseline) {
      featureEditBaselines.delete(featureId);
      return;
    }

    featureEditBaselines.set(featureId, baseline);
  };

  /**
   * 记录含 session 字段的编辑入口返回值，并保持原返回对象不变。
   *
   * @param featureId 正在编辑的 Feature ID。
   * @param payload cad-core 编辑入口返回值。
   * @returns 原始 payload。
   */
  const rememberFeatureEditPayload = <TPayload extends { readonly session: FeatureEditSession | null }>(
    featureId: string,
    payload: TPayload,
  ): TPayload => {
    rememberFeatureEditBaseline(featureId, payload.session);
    return payload;
  };

  /**
   * 规范化草图 ID。
   *
   * @param sketchId 原始草图 ID。
   * @returns 有效草图 ID；无效时返回 null。
   */
  const normalizeSketchId = (sketchId: unknown): string | null => {
    if (typeof sketchId !== 'string') {
      return null;
    }

    const trimmedSketchId = sketchId.trim();
    return trimmedSketchId.length > 0 ? trimmedSketchId : null;
  };

  /**
   * 从轮廓解析结果里读取源草图 ID。
   *
   * @param contours 轮廓解析结果。
   * @returns 源草图 ID；不存在时返回 null。
   */
  const resolveSourceSketchIdFromContours = (contours: unknown): string | null => {
    const contourList = Array.isArray(contours) ? contours : [contours];
    for (const contour of contourList) {
      if (!contour || typeof contour !== 'object') {
        continue;
      }

      const sourceSketchId = normalizeSketchId((contour as { sketchId?: unknown }).sketchId);
      if (sourceSketchId) {
        return sourceSketchId;
      }
    }

    return null;
  };

  /**
   * 从拉伸轮廓选择解析源草图 ID。
   *
   * 设计说明：
   * 拉伸提交前传入的是 PropertyManager 编辑定义，不包含源草图 ID；真正的源草图来自轮廓选择。
   * 因此必须在 commit 前通过 Viewer 轮廓解析器读出源草图，才能记录自动隐藏前的显示态。
   *
   * @param selection 拉伸选择草稿。
   * @returns 源草图 ID；不存在时返回 null。
   */
  const resolveExtrudeSourceSketchIdFromSelection = (selection: unknown): string | null => {
    if (!selection || typeof selection !== 'object') {
      return null;
    }

    const contourSnapshots = (selection as { contours?: unknown }).contours;
    const snapshotSketchId = resolveSourceSketchIdFromContours(contourSnapshots);
    if (snapshotSketchId) {
      return snapshotSketchId;
    }

    const contourIds = (selection as { contourIds?: unknown }).contourIds;
    if (!Array.isArray(contourIds)) {
      return null;
    }

    for (const contourId of contourIds) {
      if (typeof contourId !== 'string') {
        continue;
      }

      const sourceSketchId = resolveSourceSketchIdFromContours(
        requireViewer().resolveBossExtrudeSelectionContours(contourId),
      );
      if (sourceSketchId) {
        return sourceSketchId;
      }
    }

    return null;
  };

  /**
   * 从旋转轮廓选择解析源草图 ID。
   *
   * @param selection 旋转选择草稿。
   * @returns 源草图 ID；不存在时返回 null。
   */
  const resolveRevolveSourceSketchIdFromSelection = (selection: unknown): string | null => {
    if (!selection || typeof selection !== 'object') {
      return null;
    }

    const contourSnapshots = (selection as { contours?: unknown }).contours;
    const snapshotSketchId = resolveSourceSketchIdFromContours(contourSnapshots);
    if (snapshotSketchId) {
      return snapshotSketchId;
    }

    const contourIds = (selection as { contourIds?: unknown }).contourIds;
    if (!Array.isArray(contourIds)) {
      return null;
    }

    for (const contourId of contourIds) {
      if (typeof contourId !== 'string') {
        continue;
      }

      const sourceSketchId = resolveSourceSketchIdFromContours(
        requireViewer().resolveBossRevolveSelectionContours(contourId),
      );
      if (sourceSketchId) {
        return sourceSketchId;
      }
    }

    return null;
  };

  /**
   * 统一执行 feature 提交并捕获模型历史记录。
   *
   * @param input 提交输入。
   * @returns 原始提交结果。
   */
  const commitFeatureWithHistory = async <TResult extends {
    readonly featureId: string;
    readonly objectId?: string;
    readonly objectIds?: readonly string[];
  }>(input: {
    readonly featureId?: string | null;
    readonly label: string;
    readonly commandType: string;
    readonly definition?: unknown;
    readonly sourceSketchId?: string | null;
    readonly commit: () => Promise<TResult>;
  }): Promise<TResult> => {
    const editBaseline = input.featureId
      ? featureEditBaselines.get(input.featureId) ?? null
      : null;
    const result = await executeFeatureCommitWithHistory({
      viewer: requireViewer(),
      featureId: input.featureId,
      label: input.label,
      commandType: input.commandType,
      definition: input.definition,
      sourceSketchId: input.sourceSketchId,
      editBaseline,
      commit: input.commit,
      commitFeatureHistoryRecord,
    });

    if (input.featureId) {
      featureEditBaselines.delete(input.featureId);
    }
    featureEditBaselines.delete(result.featureId);

    return result;
  };

  /**
   * 将 Viewer 桥接成特征宿主视口适配器。
   */
  const extrudeViewportRef = ref<ExtrudeViewportAdapter>({
    getScene: () => getViewer()?.getScene() ?? null,
    getCamera: () => getViewer()?.getCamera() ?? null,
    getContainer: () => getViewer()?.getContainer() ?? null,
    addObject: (object: THREE.Object3D, parent?: THREE.Object3D, index?: number) => {
      return requireViewer().addObject(object, parent, index);
    },
    setExtrudePreviewMesh: async (
      meshData: unknown | null,
      context?: {
        prototype?: FeaturePrototype | null;
        draftDefinition?: ExtrudeFeatureDefinitionModel | null;
      },
    ) => {
      await (getViewer()?.setExtrudePreviewMesh(meshData as never, context as never) ?? Promise.resolve(null));
    },
    setFeaturePreviewMesh: async (
      meshData: unknown | null,
      context?: {
        prototype?: FeaturePrototype | null;
        draftDefinition?: FeatureDefinition | null;
      },
    ) => {
      await (getViewer()?.setExtrudePreviewMesh(meshData as never, context as never)
        ?? Promise.resolve(null));
    },
    addMesh: async (
      meshData: unknown,
      context?: {
        prototype?: FeaturePrototype | null;
      },
    ) => {
      await (getViewer()?.addResultMesh(meshData as never, context as never) ?? Promise.resolve(null));
    },
    removeMesh: (meshId: string) => {
      getViewer()?.removeResultMesh(meshId);
      getViewer()?.removeSceneObject(meshId);
    },
    setSelectionMode: (mode: string, options?: Record<string, unknown>) => {
      getViewer()?.setSelectionMode?.(mode as 'object' | 'face' | 'edge' | 'vertex' | 'face-edge' | 'topology', options as { preserveSelection?: boolean } | undefined);
    },
    getSelectionMode: () => getViewer()?.getSelectionMode?.() ?? 'object',
    getFeatureTopologySelectionMode: () => (
      getViewer()?.getFeatureTopologySelectionMode?.() ?? 'none'
    ),
    setAllowOccludedFeatureTopologySelection: (value: boolean) => {
      getViewer()?.setAllowOccludedFeatureTopologySelection?.(value);
    },
    getAllowOccludedFeatureTopologySelection: () => (
      getViewer()?.getAllowOccludedFeatureTopologySelection?.() ?? false
    ),
    resolveRuntimeSketchId: (sketchId: string) => (
      getViewer()?.resolveRuntimeSketchId(sketchId) ?? null
    ),
    setSketchRegionSelectionMode: () => {},
    setSketchRegionHover: () => {},
    setSketchRegionSelected: () => {},
    enableRevolveRegionSelection: async (sketchId: string) => {
      await (getViewer()?.enableRevolveRegionSelection?.(sketchId) ?? Promise.resolve());
    },
    disableRevolveRegionSelection: () => {
      getViewer()?.disableRevolveRegionSelection?.();
    },
    refreshRevolveRegionsForSketch: async (sketchId: string) => {
      await (getViewer()?.refreshRevolveRegionsForSketch?.(sketchId) ?? Promise.resolve());
    },
    getRevolveRegions: (sketchId: string) => (
      getViewer()?.getRevolveRegions?.(sketchId) ?? []
    ),
    getRevolveSketchRuntimeInput: (sketchId: string) => (
      getViewer()?.getRevolveSketchRuntimeInput?.(sketchId) ?? null
    ),
    setRevolveRegionHover: (regionId: string | null) => {
      getViewer()?.setRevolveRegionHover?.(regionId);
    },
    setRevolveRegionSelections: (regionIds: string[]) => {
      getViewer()?.setRevolveRegionSelections?.(regionIds);
    },
    setRevolveRegionFillVisible: (visible: boolean) => {
      getViewer()?.setRevolveRegionFillVisible?.(visible);
    },
    pickRevolveRegionFromEvent: (event: MouseEvent) => (
      getViewer()?.pickRevolveRegionFromEvent?.(event) ?? null
    ),
    enableHover: () => {},
    getSelectedFaceInfo: () => getViewer()?.getSelectedFaceInfo?.() ?? null,
    getSelectedObjectIds: () => getViewer()?.getSelectedObjectIds() ?? [],
    getSelectedObjects: () => getViewer()?.getSelectedObjects?.() ?? [],
    selectFeatureTopologyItems: (items) => {
      getViewer()?.selectFeatureTopologyItems?.(items as never);
    },
    resolveExtrudeEndTargetDisplay: async (target) => await getViewer()?.resolveExtrudeEndTargetDisplay(target) ?? null,
    setExtrudeEndTargetVisual: (target, mode) => { getViewer()?.setExtrudeEndTargetVisual(target, mode); },
    getPrimarySelectedObject: () => getViewer()?.getPrimarySelectedObject?.() ?? null,
    getSceneObjectById: (objectId: string) => getViewer()?.getSceneObjectById(objectId) ?? null,
    resolveObjectWorldPoint: (objectId: string, point: [number, number, number]) => (
      getViewer()?.resolveObjectWorldPoint?.(objectId, point) ?? null
    ),
    getTopologyEdgeSegment: (objectId: string, edgeIndex: number) => (
      getViewer()?.getTopologyEdgeSegment?.(objectId, edgeIndex) ?? null
    ),
    startFeatureEditSession: async (featureId: string) => {
      const session = await (
        getViewer()?.startFeatureEditSession(featureId)
        ?? Promise.resolve<FeatureEditSession | null>(null)
      );
      rememberFeatureEditBaseline(featureId, session);
      return session;
    },
    getFeaturePrototypeById: async (featureId: string) => (
      await (getViewer()?.getFeaturePrototypeById(featureId) ?? Promise.resolve<FeaturePrototype | null>(null))
    ),
    closeFeatureEditSession: (featureId: string) => {
      featureEditBaselines.delete(featureId);
      getViewer()?.closeFeatureEditSession(featureId);
    },
    resolveBossExtrudeSelectionContours: (objectId: string) => (
      getViewer()?.resolveBossExtrudeSelectionContours(objectId) ?? null as BossExtrudeSelectionResolution | null
    ),
    resolveCutExtrudePreviewSeed: (input) => (
      getViewer()?.resolveCutExtrudePreviewSeed?.(input as never) ?? null
    ),
    resolveBossRevolveSelectionContours: (objectId: string) => (
      getViewer()?.resolveBossRevolveSelectionContours(objectId) ?? null
    ),
    resolveBossRevolveAxisSelection: (input) => (
      getViewer()?.resolveBossRevolveAxisSelection(input as never) ?? null
    ),
    startBossExtrudeEditSession: async (featureId: string) => {
      const currentViewer = getViewer();
      if (!currentViewer) {
        return rememberFeatureEditPayload(featureId, {
          session: null,
          definition: null,
          contours: [],
        });
      }
      return rememberFeatureEditPayload(featureId, await currentViewer.startBossExtrudeEditSession(featureId));
    },
    startExtrudeEditSession: async (featureId: string) => {
      const currentViewer = getViewer();
      if (!currentViewer) {
        return rememberFeatureEditPayload(featureId, {
          session: null,
          definition: null,
          contours: [],
        });
      }
      return rememberFeatureEditPayload(featureId, await currentViewer.startExtrudeEditSession(featureId));
    },
    previewBossExtrudeFeature: async (input) => {
      return requireViewer().previewBossExtrudeFeature(input as never);
    },
    previewExtrudeFeature: async (input) => {
      return requireViewer().previewExtrudeFeature(input as never);
    },
    commitBossExtrudeFeature: async (input) => {
      return commitFeatureWithHistory({
        featureId: input.featureId,
        label: input.featureId ? '编辑拉伸凸台' : '创建拉伸凸台',
        commandType: 'feature.commit.boss-extrude',
        definition: input.definition as never,
        sourceSketchId: resolveExtrudeSourceSketchIdFromSelection(input.selection),
        commit: () => requireViewer().commitBossExtrudeFeature(input as never),
      });
    },
    commitExtrudeFeature: async (input) => {
      return commitFeatureWithHistory({
        featureId: input.featureId,
        label: input.featureId ? '编辑拉伸特征' : '创建拉伸特征',
        commandType: 'feature.commit.extrude',
        definition: input.definition as never,
        sourceSketchId: resolveExtrudeSourceSketchIdFromSelection(input.selection),
        commit: () => requireViewer().commitExtrudeFeature(input as never),
      });
    },
    syncFeatureMeshes: async (options?: {
      preservePreviewMeshes?: boolean;
    }) => {
      const currentViewer = requireViewer();
      const summary = await currentViewer.getGeometryStateSummary({syncHistoryProjection: false});
      await currentViewer.syncFeatureMeshesFromState(summary, {
        preservePreviewMeshes: options?.preservePreviewMeshes === true,
      });
    },
    startBossRevolveEditSession: async (featureId: string): Promise<BossRevolveEditSessionPayload> => {
      const currentViewer = getViewer();
      if (!currentViewer) {
        return rememberFeatureEditPayload(featureId, {
          session: null,
          definition: null,
          selectedContours: [],
          selectedAxisLabel: null,
        });
      }
      return rememberFeatureEditPayload(featureId, await currentViewer.startBossRevolveEditSession(featureId));
    },
    startCutRevolveEditSession: async (featureId: string): Promise<BossRevolveEditSessionPayload> => {
      const currentViewer = getViewer();
      if (!currentViewer) {
        return rememberFeatureEditPayload(featureId, {
          session: null,
          definition: null,
          selectedContours: [],
          selectedAxisLabel: null,
        });
      }
      return rememberFeatureEditPayload(featureId, await currentViewer.startCutRevolveEditSession(featureId));
    },
    previewBossRevolveFeature: async (input) => {
      return requireViewer().previewBossRevolveFeature(input as never);
    },
    commitBossRevolveFeature: async (input) => {
      return commitFeatureWithHistory({
        featureId: input.featureId,
        label: input.featureId ? '编辑旋转凸台' : '创建旋转凸台',
        commandType: 'feature.commit.boss-revolve',
        definition: input.definition as never,
        sourceSketchId: resolveRevolveSourceSketchIdFromSelection(input.selection),
        commit: () => requireViewer().commitBossRevolveFeature(input as never),
      });
    },
    previewCutRevolveFeature: async (input) => {
      return requireViewer().previewCutRevolveFeature(input as never);
    },
    setRevolveCutKeepBodyPreviewFragments: (fragmentMeshes) => {
      getViewer()?.setRevolveCutKeepBodyPreviewFragments?.(fragmentMeshes);
    },
    updateRevolveCutKeepBodyPreviewSelection: (selection) => {
      getViewer()?.updateRevolveCutKeepBodyPreviewSelection?.(selection);
    },
    clearRevolveCutKeepBodyPreview: () => {
      getViewer()?.clearRevolveCutKeepBodyPreview?.();
    },
    commitCutRevolveFeature: async (input) => {
      return commitFeatureWithHistory({
        featureId: input.featureId,
        label: input.featureId ? '编辑旋转切除' : '创建旋转切除',
        commandType: 'feature.commit.cut-revolve',
        definition: input.definition as never,
        sourceSketchId: resolveRevolveSourceSketchIdFromSelection(input.selection),
        commit: () => requireViewer().commitCutRevolveFeature(input as never),
      });
    },
    updateBossRevolveAngleDrag: (input) => {
      return requireViewer().updateBossRevolveAngleDrag(input as never);
    },
    stopBossRevolveAngleDrag: () => {
      getViewer()?.stopBossRevolveAngleDrag();
    },
    startFilletEditSession: async (featureId: string): Promise<FilletEditSessionPayload> => {
      const currentViewer = getViewer();
      if (!currentViewer) {
        return rememberFeatureEditPayload(featureId, {
          session: null,
          definition: null,
          upstreamBodyObjectId: null,
          name: null,
        });
      }
      return rememberFeatureEditPayload(featureId, await currentViewer.startFilletEditSession(featureId));
    },
    previewFilletFeature: async (input: {
      featureId?: string | null;
      definition: FilletFeatureDefinition;
    }) => {
      return requireViewer().previewFilletFeature(input as never);
    },
    commitFilletFeature: async (input: {
      featureId?: string | null;
      definition: FilletFeatureDefinition;
    }) => {
      return commitFeatureWithHistory({
        featureId: input.featureId,
        label: input.featureId ? '编辑圆角' : '创建圆角',
        commandType: 'feature.commit.fillet',
        definition: input.definition,
        commit: () => requireViewer().commitFilletFeature(input as never),
      });
    },
    startChamferEditSession: async (featureId: string): Promise<ChamferEditSessionPayload> => {
      const currentViewer = getViewer();
      if (!currentViewer) {
        return rememberFeatureEditPayload(featureId, {
          session: null,
          definition: null,
          upstreamBodyObjectId: null,
          name: null,
        });
      }
      return rememberFeatureEditPayload(featureId, await currentViewer.startChamferEditSession(featureId));
    },
    previewChamferFeature: async (input: {
      featureId?: string | null;
      definition: ChamferFeatureDefinition;
    }) => {
      return requireViewer().previewChamferFeature(input as never);
    },
    commitChamferFeature: async (input: {
      featureId?: string | null;
      definition: ChamferFeatureDefinition;
    }) => {
      return commitFeatureWithHistory({
        featureId: input.featureId,
        label: input.featureId ? '编辑倒角' : '创建倒角',
        commandType: 'feature.commit.chamfer',
        definition: input.definition,
        commit: () => requireViewer().commitChamferFeature(input as never),
      });
    },
    executeFeaturePreview: async (input: {
      featureKind: string;
      featureId?: string | null;
      definition: FeatureDefinition;
      bindings: SerializedFeatureReferenceBinding[];
      preferredItemRef?: string | null;
    }) => {
      return requireViewer().executeFeaturePreview(input as never);
    },
    requestFeaturePreview: async (input: {
      featureKind: string;
      featureId?: string | null;
      definition: FeatureDefinition;
      bindings: SerializedFeatureReferenceBinding[];
      preferredItemRef?: string | null;
    }, options?: { force?: boolean }) => {
      return requireViewer().requestFeaturePreview(input as never, options);
    },
    clearFeaturePreviewRequests: () => {
      requireViewer().clearFeaturePreviewRequests();
    },
    commitFeature: async (input: {
      featureKind: string;
      featureId?: string | null;
      definition: FeatureDefinition;
      bindings: SerializedFeatureReferenceBinding[];
    }) => {
      return commitFeatureWithHistory({
        featureId: input.featureId,
        label: input.featureId ? '编辑特征' : '创建特征',
        commandType: `feature.commit.${input.featureKind}`,
        definition: input.definition,
        commit: () => requireViewer().commitFeature(input as never),
      });
    },
    subscribeSketchDefinitionDirty: (sketchId, handler) => {
      return getViewer()?.observeSketchDefinitionDirty?.(sketchId, handler) ?? (() => {});
    },
  });

  return {
    extrudeViewportRef,
  };
}
